import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { TrendingDown, Activity, Calendar, Award, BarChart3 } from "lucide-react";
import { MUSCLE_LABELS } from "../components/BodyDiagram";

export default function Insights() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/insights").then((r) => setData(r.data));
  }, []);

  if (!data) return <Layout><div className="text-ink-500">Loading…</div></Layout>;

  const maxMuscleCount = Math.max(1, ...(data.top_muscles.map((m) => m.count)));

  return (
    <Layout>
      <div className="label-overline mb-2">Pattern insights</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900 mb-2">What's working</h1>
      <p className="text-ink-700 mb-10 max-w-2xl">Across all clients & sessions. The more outcomes you track, the sharper these signals get.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Stat icon={<Calendar className="w-4 h-4" />} label="Total sessions" value={data.total_sessions} />
        <Stat icon={<BarChart3 className="w-4 h-4" />} label="With outcomes" value={data.sessions_with_outcomes} />
        <Stat icon={<TrendingDown className="w-4 h-4" />} label="Avg pain drop" value={data.avg_pain_drop !== null ? `${data.avg_pain_drop} / 10` : "—"} highlight />
        <Stat icon={<Award className="w-4 h-4" />} label="Avg effectiveness" value={data.avg_effectiveness !== null ? `${data.avg_effectiveness} / 5` : "—"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4 text-terracotta-500" />
            <div className="label-overline text-terracotta-600">Top performing techniques</div>
          </div>
          <p className="text-sm text-ink-700 mb-6">Ranked by average pain drop. Track outcomes to surface your best protocols.</p>
          {data.top_techniques.length === 0 ? (
            <div className="text-ink-500 py-8 text-center">No technique data yet — finish a session with outcomes to populate.</div>
          ) : (
            <div className="space-y-3">
              {data.top_techniques.map((t, i) => (
                <div key={t.technique} className="flex items-center justify-between gap-4 py-2 border-b border-paper-200 last:border-0" data-testid={`technique-row-${i}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="w-7 h-7 rounded-full bg-paper-100 flex items-center justify-center text-xs font-heading text-ink-700">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="font-medium text-ink-900 truncate">{t.technique}</div>
                      <div className="text-xs text-ink-500">{t.uses} {t.uses === 1 ? "use" : "uses"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {t.avg_pain_drop !== null && (
                      <div className="text-right">
                        <div className="font-heading text-lg text-sage-600">↓ {t.avg_pain_drop}</div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-ink-500">pain drop</div>
                      </div>
                    )}
                    {t.avg_effectiveness && (
                      <div className="text-right">
                        <div className="font-heading text-lg text-terracotta-500">{t.avg_effectiveness}</div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-ink-500">avg ★</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-sage-600" />
            <div className="label-overline">Most-worked muscle groups</div>
          </div>
          <p className="text-sm text-ink-700 mb-6">What you're actually treating in practice.</p>
          {data.top_muscles.length === 0 ? (
            <div className="text-ink-500 py-8 text-center">No data yet.</div>
          ) : (
            <div className="space-y-3">
              {data.top_muscles.map((m) => (
                <div key={m.muscle} data-testid={`muscle-row-${m.muscle}`}>
                  <div className="flex items-center justify-between mb-1.5 text-sm">
                    <span className="text-ink-900 font-medium">{MUSCLE_LABELS[m.muscle] || m.muscle}</span>
                    <span className="text-ink-500">{m.count}</span>
                  </div>
                  <div className="h-2 bg-paper-100 rounded-full overflow-hidden">
                    <div className="h-full bg-sage-500 rounded-full transition-all" style={{ width: `${(m.count / maxMuscleCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {data.trend.length > 0 && (
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-10">
          <div className="label-overline mb-1">Recent trend</div>
          <p className="text-sm text-ink-700 mb-6">Pain before (terracotta) vs after (sage) across your last {data.trend.length} sessions.</p>
          <TrendBars trend={data.trend} />
        </div>
      )}
    </Layout>
  );
}

function Stat({ icon, label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-5 border ${highlight ? "bg-terracotta-500 border-terracotta-500 text-white" : "bg-white border-paper-200"}`}>
      <div className="flex items-center gap-2 mb-2 opacity-80">{icon}<span className="text-xs uppercase tracking-[0.14em]">{label}</span></div>
      <div className={`font-heading text-2xl font-medium ${highlight ? "" : "text-ink-900"}`}>{value}</div>
    </div>
  );
}

function TrendBars({ trend }) {
  const max = 10;
  return (
    <div className="flex items-end gap-1.5 h-40 overflow-x-auto pb-2">
      {trend.map((t, i) => {
        const pb = t.pain_before ?? 0;
        const pa = t.pain_after ?? 0;
        return (
          <div key={`trend-${t.created_at || i}`} className="flex flex-col items-center gap-1 min-w-[24px]">
            <div className="flex gap-0.5 items-end h-32">
              <div className="w-2.5 bg-terracotta-500 rounded-t" style={{ height: `${(pb / max) * 100}%` }} title={`Before: ${pb}`} />
              <div className="w-2.5 bg-sage-500 rounded-t" style={{ height: `${(pa / max) * 100}%` }} title={`After: ${pa}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
